import { getPayItems } from '../../utils/getPayItems'
import { getReport } from '../../utils/getReport'
import { useEffect } from 'react'
import CIMSSelect from 'components/CIMSSelect'
import CIMSFormItem from 'components/CIMSFormItem'
import ReviewNotesFormItem from '../ReviewNotesFormItem'
import { Action, ValidatedResults } from './types'
import FormlessModal from 'components/CIMSModal/FormlessModal'
import { useForm, useWatch } from 'antd/lib/form/Form'
import { Form } from 'antd'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import {
  useDraftEngineeringJudgementMutation,
  useReviewIdentifiedFieldIssueMutation,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import {
  simpleErrorNotification,
  simpleSuccessNotification,
} from 'hooks/notificationHandler'
import { useIFITrackerContext } from '../../IFITrackerContext'
import { useCreateIssueFormItems } from 'App/components/ProjectRoutes/components/issues/hooks/useCreateIssueFormItems'
import { useIssueReportsSelectOptionsContext } from '../../../../IssueReportsSelectOptionsContext'
import ExternalLinkEditor from 'components/ExternalLinkEditor'
import CommonEJFormItems from '../../../../../EngineeringJudgements/components/EJLog/components/EJLogTable/components/CommonEJFormItems'
import CreateEditEJFormItems from '../../../../../EngineeringJudgements/components/EJLog/components/EJLogTable/components/CreateEditEJFormItems'
import { useEJReportsSelectOptionsContext } from '../../../../../EngineeringJudgements/components/EJReportsSelectOptionsContext'
import { EJReportType, IFIReportType } from '@cims-online/shared'
import { errorHandler } from 'hooks/errorHandler'
import { useProjectContext } from 'context/ProjectContext'

const ReviewModal: React.FC = () => {
  const { projectId } = useProjectContext()
  const {
    refetch,
    review: { state, toggle, visible },
    quickLinkOptions,
    ejMetadata: { loading: ejMetaFetching, ejTypes, ejQuickLinks },
    ejTypesOptions,
  } = useIFITrackerContext()
  const { toLabeledValues: toPayItems, fromLabeledValues: fromPayItems } =
    usePayItemSelectContext()
  const { fromLabeledValues: fromReports } = useEJReportsSelectOptionsContext()
  const [form] = useForm<ValidatedResults>()
  const action = useWatch('action', form)
  const typeId = useWatch('typeId', form)
  const payItems = useWatch('payItems', form)
  useEffect(() => {
    /**
     * have to add a callback in the catch to avoid uncaught promise errors, even
     * though it's optional in the catch docs
     */
    form.validateFields().catch(() => {})
  }, [action, form])
  const { toLabeledValues: toReports } = useIssueReportsSelectOptionsContext()
  const number = crypto.randomUUID()
  useEffect(() => {
    const reports = state
      ? toReports([
          state?.densityTest ? state.densityTest.lot : getReport(state),
        ])
      : []
    if (action === Action.Issue)
      form.setFieldsValue({
        description: state?.reportDescription,
        payItems: toPayItems(getPayItems(state)),
        reports,
      })
    else if (action === Action.EJ) {
      const ejTypeReportType = EJReportType.fromId(
        ejTypes?.find(({ id }) => typeId === id)?.reportTypeId
      )?.classType
      const ifiReportType = IFIReportType.fromId(state?.reportTypeId)?.classType
      if (payItems === undefined)
        form.setFieldValue('payItems', toPayItems(getPayItems(state)))
      /**
       * if the selected EJ Type's report type matches the IFI's report type,
       * set the initial value of reports to the IFI's report
       * OR
       * if the EJ Type's report type is 'Lot', and the IFI's report type is 'Density Test',
       * set the Lot associated with the Density Test as the initial value for reports
       */
      if (
        ejTypeReportType === ifiReportType ||
        (ejTypeReportType === 'Lot' && ifiReportType === 'DensityTest')
      )
        form.setFieldValue('reports', reports)
      else form.setFieldValue('reports', [])
    } else if (action === Action.Close)
      form.setFieldValue('reviewNotes', state?.reviewNotes)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, form, action, typeId, toPayItems, toReports])
  const {
    CreateIssueFormItems,
    ModalFooter,
    mutating,
    setMutating,
    draftSubmit,
    cleanUp,
  } = useCreateIssueFormItems({
    number,
    form,
    cleanup: true,
    toggle,
  })
  const [review] = useReviewIdentifiedFieldIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      reviewIdentifiedFieldIssue: {
        identifiedFieldIssue: { reportName },
      },
    }) => simpleSuccessNotification(`${reportName} successfully reviewed!`),
  })
  const [draftEJ] = useDraftEngineeringJudgementMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      draftEngineeringJudgement: {
        judgement: { formattedNumber },
      },
    }) => simpleSuccessNotification(`${formattedNumber} drafted successfully!`),
  })
  return (
    <FormlessModal
      afterClose={() => form.resetFields()}
      title={`Review ${state?.reportName}`}
      visible={visible}
      cancel={{ fn: () => cleanUp() }}
      ok={{
        title: action === Action.EJ ? 'Draft' : 'Ok',
        fn: () => form.submit(),
        props: { disabled: !action || mutating },
        loading: mutating,
      }}
      footer={action === Action.Issue ? ModalFooter : undefined}
    >
      <Form
        form={form}
        onFinish={async (results) => {
          if (!state) return errorHandler('state is not defined')
          setMutating(true)
          if (results.action === undefined)
            simpleErrorNotification('No action found in results')
          else if (results.action === Action.Close) {
            const { reviewNotes } = results
            await review({
              variables: { input: { reviewNotes, reportId: state.reportId } },
            })
            refetch()
          } else if (results.action === Action.Issue) {
            await draftSubmit({
              ...results,
              number,
              identifiedFieldIssueId: state!.reportId,
            })
          } else if (results.action === Action.EJ) {
            const {
              action: _,
              failureDate,
              reports,
              payItems,
              ...rest
            } = results
            await draftEJ({
              variables: {
                input: {
                  projectId,
                  identifiedFieldIssueId: state.reportId,
                  // Failure date is a required field, so using bang
                  failureDate: failureDate!.toISOString(),
                  reports: fromReports(reports),
                  payItemIds: fromPayItems(payItems),
                  ...rest,
                },
              },
            })
          }
          refetch()
          setMutating(false)
          toggle(null)
        }}
      >
        <CIMSFormItem
          name="action"
          label="Action"
          help="The desired action to take for this Review"
        >
          <CIMSSelect
            simple
            options={Object.values(Action).map((value) => ({
              label: value,
              value,
            }))}
          />
        </CIMSFormItem>
        {action === Action.Issue && (
          <>
            {CreateIssueFormItems}
            <ExternalLinkEditor quickLinkOptions={quickLinkOptions} />
          </>
        )}
        {action === Action.Close && (
          <ReviewNotesFormItem
            requiredMessage={`Please add review notes before closing ${state?.reportName}`}
          />
        )}
        {action === Action.EJ && (
          <>
            <CommonEJFormItems
              form={form}
              types={ejTypes}
              typeOptions={ejTypesOptions}
            />
            <CreateEditEJFormItems
              loading={ejMetaFetching}
              quickLinks={ejQuickLinks}
            />
          </>
        )}
      </Form>
    </FormlessModal>
  )
}

export default ReviewModal
